/**
 * useEditFlow — generic inline-edit lifecycle.
 *
 * Wraps the four-state edit dance: read-mode → toggle → edit-mode
 * (with draft mutations) → save (with loading + error) → reset on
 * success/cancel. Generic over the draft shape so Step 1 (item
 * definition), Step 2 (asset list), etc. can each reuse it.
 *
 * Path-based mutation:
 *   - `onChange(path, value)` does an immutable deep set on the
 *     draft tree. Paths are arrays of string keys and/or numeric
 *     indices, so editing nested object fields and array rows uses
 *     the same primitive.
 *
 * The caller provides:
 *   - `source` — the underlying value (e.g. the structured fetch).
 *     Toggling into edit mode deep-clones this as the working draft.
 *   - `onSave(draft)` — async persistence callback. Called with
 *     the current draft; the hook handles loading + error states.
 *     On success the hook exits edit mode and clears the draft.
 */
import { useCallback, useState } from 'react';
import type { EditFlowState } from '../step-components/stepBodyProps';

/**
 * Deep clone the source tree before exposing it as a mutable draft.
 *
 * Story H5 — prefer the structured-clone algorithm so `Date`,
 * `Map`/`Set`, typed arrays, and circular refs survive correctly.
 * Falls back to JSON round-trip on environments without
 * `structuredClone` (Vitest jsdom < 22 and a few older test runners).
 * The JSON fallback is the behaviour shipped before H5 — drops
 * Dates/undefined, throws on circular — fine for the Step 1 trees
 * that are plain JSON today; the upgrade simply lifts the contract.
 */
function cloneDraft<T>(source: T): T {
  if (typeof structuredClone === 'function') return structuredClone(source);
  return JSON.parse(JSON.stringify(source)) as T;
}

/**
 * Immutable deep set — produces a new tree with the value at `path`
 * replaced. Empty path returns the value directly.
 */
function setDeep(obj: any, path: (string | number)[], value: unknown): any {
  if (!path || path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(obj)) {
    const out = obj.slice();
    out[head as number] = setDeep(out[head as number], rest, value);
    return out;
  }
  return { ...(obj ?? {}), [head]: setDeep(obj?.[head], rest, value) };
}

export interface UseEditFlowInput<T> {
  source: T | null;
  onSave: (draft: T) => Promise<void>;
}

export function useEditFlow<T = unknown>({
  source,
  onSave,
}: UseEditFlowInput<T>): EditFlowState<T> {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<T | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const onToggle = useCallback(() => {
    if (source == null) return;
    setDraft(cloneDraft(source));
    setError(null);
    setIsEditing(true);
  }, [source]);

  const onCancel = useCallback(() => {
    setIsEditing(false);
    setDraft(null);
    setError(null);
  }, []);

  const onChange = useCallback((path: (string | number)[], value: unknown) => {
    setDraft((prev) => setDeep(prev, path, value) as T);
  }, []);

  const onSaveCb = useCallback(async () => {
    if (draft == null) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setIsEditing(false);
      setDraft(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  }, [draft, onSave]);

  return {
    isEditing,
    draft,
    saving,
    error,
    onToggle,
    onChange,
    onSave: onSaveCb,
    onCancel,
  };
}
